import {
  InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  QueryKey,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { keys as calendarKeys } from 'entities/calendar';
import { CommonError } from 'shared/api/types';
import { handleRequestError } from 'shared/lib/axios';
import { queryClient } from 'shared/lib/react-query';

import { activateCountry, deactivateCountry, getCountryList } from './requests';
import { CountryListRequest, CountryListDTO } from './types';

export const keys = {
  root: () => ['countries'] as const,
  list: () => [...keys.root(), 'list'] as const,
  listWithFilters: (filters: Omit<CountryListRequest, 'page'>) => [...keys.list(), 'listWithFilters', filters] as const,
  update: (id: string) => ['update-country', id] as const,
};

export const CountryService = {
  queryKey: (filters: Omit<CountryListRequest, 'page'>) => keys.listWithFilters(filters),

  getCache: (filters: Omit<CountryListRequest, 'page'>) =>
    queryClient.getQueryData<InfiniteData<CountryListDTO>>(CountryService.queryKey(filters)),

  queryOptions: (filters: Omit<CountryListRequest, 'page'>) => {
    const countryKey = CountryService.queryKey(filters);
    return infiniteQueryOptions({
      queryKey: countryKey,
      queryFn: async ({ pageParam, signal }) => {
        const data = await getCountryList({ ...filters, page: pageParam.page }, { signal });

        return data;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      initialPageParam: { page: 0 } as any,
      getNextPageParam: (lastPage) => {
        if (!lastPage.pageable || lastPage.totalPages <= lastPage.pageable.pageNumber + 1) {
          return null;
        }
        return {
          page: lastPage.pageable.pageNumber + 1,
        };
      },
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      initialData: () => CountryService.getCache(filters)!,
      initialDataUpdatedAt: () => queryClient.getQueryState(countryKey)?.dataUpdatedAt,
      placeholderData: keepPreviousData,
    });
  },
  prefetchQuery: async (filters: Omit<CountryListRequest, 'page'>) =>
    queryClient.prefetchInfiniteQuery(CountryService.queryOptions(filters)),
};

export const useUpdateCountry = (
  id: string,
): UseMutationResult<
  void,
  CommonError,
  { id: string; status: boolean },
  { previousData: [QueryKey, InfiniteData<CountryListDTO> | undefined][] }
> => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: keys.update(id),
    mutationFn: ({ status }) => (status ? activateCountry(id) : deactivateCountry(id)),
    onMutate: async () => {
      await qClient.cancelQueries({ queryKey: keys.list() });

      const previousData = qClient.getQueriesData<InfiniteData<CountryListDTO>>({ queryKey: keys.list() });

      previousData.forEach(([queryKey, data]) => {
        if (data) {
          qClient.setQueryData<InfiniteData<CountryListDTO>>(queryKey, {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              content: page.content.map((country) => {
                return country.id === id ? { ...country, active: !country.active } : country;
              }),
            })),
          });
        }
      });

      return { previousData };
    },
    onError: (error, _, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qClient.setQueryData(queryKey, data);
        });
      }
      handleRequestError(error);
    },
    onSettled: () => {
      qClient.invalidateQueries({ queryKey: keys.list() });
      qClient.invalidateQueries({ queryKey: [calendarKeys.root] });
    },
  });
};
